  (function () {
    if (!window.DrawerDialog) return;

    /**
     * Full-screen mobile search drawer. The shell (modality, scroll-lock,
     * focus, Escape, backdrop-click, view-transition morph) is provided by
     * DrawerDialog + themeUtils.surfaces; this subclass owns only the Athos
     * search engine, wired through the onOpen(detail)/onClose() hooks.
     */
    class MobileSearchDrawer extends window.DrawerDialog {
      /** @returns {string} the registered surface id. */
      get surfaceName() {
        return 'mobile-search';
      }

      /** @returns {boolean} declares a search surface so the manager emits search:*. */
      get isSearch() {
        return true;
      }

      /** Registers the surface (base) then wires the search engine once. */
      connectedCallback() {
        super.connectedCallback();
        if (this._searchInitialised) return;
        this._searchInitialised = true;
        this.#initEngine();
      }

      /**
       * Builds the Athos search engine and binds the onOpen/onClose hooks.
       * Ported verbatim from the previous implementation minus the drawer
       * chrome (now owned by the base/manager).
       * @returns {void}
       */
      #initEngine() {
        const host = this;
        const utils = window.themeUtils;
        if (!utils) return;

        const debounce = utils.debounce;

        /* ── Configuration ──────────────────────────── */

        const MIN_QUERY_LENGTH = 3;
        const PRODUCT_COUNT = 6;
        const SEARCH_SPRING_TAG = host.dataset.athosId;
        const MONEY_FORMAT = host.dataset.moneyFormat;
        const I18N_NO_TOP_SEARCHES = host.dataset.i18nNoTopSearches;

        /* ── DOM Cache ──────────────────────────────── */

        const searchInput = host.querySelector('[data-msearch-input]');
        const clearBtn = host.querySelector('[data-msearch-clear]');
        const topList = host.querySelector('[data-msearch-top-list]');
        const recentList = host.querySelector('[data-msearch-recent-list]');
        const recentEmptyTemplate = host.querySelector('[data-msearch-recent-empty-template]');
        const suggestionsCol = host.querySelector('[data-msearch-col="suggestions"]');
        const suggestionsList = host.querySelector('[data-msearch-suggestions-list]');
        const productsContainer = host.querySelector('[data-msearch-products]');
        const noResultsEl = host.querySelector('[data-msearch-no-results]');
        const loader = host.querySelector('[data-msearch-loader]');
        const viewAllBtn = host.querySelector('[data-msearch-view-all]');
        const totalCountEl = host.querySelector('[data-msearch-total-count]');
        const termTemplate = host.querySelector('[data-msearch-term-template]');
        const productTemplate = host.querySelector('[data-msearch-product-template]');
        const skeletonTemplate = host.querySelector('[data-msearch-skeleton-template]');
        const emptyTemplate = host.querySelector('[data-msearch-empty-template]');

        /* ── State ──────────────────────────────────── */

        let activeAbortController = null;
        let activeObserver = null;
        let defaultProducts = null;
        let pendingRedirect = null;

        /* ── Helpers ─────────────────────────────────── */

        /**
         * Build a responsive srcset for an Athos image URL.
         * @param {string} url
         * @param {number[]} widths
         * @returns {string}
         */
        function buildSrcset(url, widths) {
          return window.AthosAPI.buildSrcset(url, widths);
        }

        /* ── History Management ──────────────────────── */

        /**
         * Render the recent-search list from local history.
         * @returns {void}
         */
        function renderRecent() {
          const history = window.AthosAPI.getHistory();

          if (history.length === 0) {
            recentList.replaceChildren();
            recentList.appendChild(recentEmptyTemplate.content.cloneNode(true));
            return;
          }

          const frag = document.createDocumentFragment();
          history.forEach(function (term) {
            if (!term) return;
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });

          recentList.replaceChildren();
          recentList.appendChild(frag);
        }

        /* ── Trending (Top Searches) ────────────────── */

        /**
         * Fetch the trending search terms from Athos.
         * @returns {Promise<string[]>}
         */
        async function fetchTrending() {
          return window.AthosAPI.fetchTrending();
        }

        /**
         * Render the trending (top searches) list.
         * @param {string[]} terms
         * @returns {void}
         */
        function renderTrending(terms) {
          topList.replaceChildren();

          if (terms.length === 0) {
            const li = document.createElement('li');
            li.textContent = I18N_NO_TOP_SEARCHES;
            topList.appendChild(li);
            return;
          }

          const frag = document.createDocumentFragment();
          terms.forEach(function (term) {
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });
          topList.appendChild(frag);
        }

        /* ── Default Recommended Products ───────────── */

        /**
         * Fetch the default recommended products for the configured profile.
         * @returns {Promise<Object|null>}
         */
        async function fetchDefaultProducts() {
          if (!SEARCH_SPRING_TAG) return null;
          const profiles = await window.AthosAPI.fetchRecommend({ profiles: [SEARCH_SPRING_TAG] });
          const profile = profiles && profiles[0] ? profiles[0] : null;
          if (!profile || !profile.results) return null;
          profile.results = profile.results.slice(0, PRODUCT_COUNT).map(window.AthosAPI.normaliseProductResult);
          await primePresentmentPrices(profile.results);
          return profile;
        }

        /**
         * Prime the Storefront presentment-price cache for a product set so
         * renderProducts can overlay exact market prices synchronously.
         * No-op for base-currency shoppers.
         * @param {Object[]} products
         * @returns {Promise<void>}
         */
        async function primePresentmentPrices(products) {
          if (!window.AthosAPI || typeof window.AthosAPI.fetchPresentmentPrices !== 'function') return;
          const handles = (products || [])
            .map(function (p) {
              return p.handle || '';
            })
            .filter(Boolean);
          if (handles.length === 0) return;
          await window.AthosAPI.fetchPresentmentPrices({ handles });
        }

        /* ── Search Query (Suggestions + Products) ──── */

        /**
         * Fetch autocomplete suggestions + products for a query.
         * @param {string} query
         * @returns {Promise<Object>}
         */
        async function fetchSuggestions(query) {
          if (activeAbortController) activeAbortController.abort();
          activeAbortController = new AbortController();

          if (!window.AthosAPI) return { error: true };

          try {
            const result = await window.AthosAPI.fetchSuggest({
              query,
              limit: PRODUCT_COUNT,
              signal: activeAbortController.signal,
            });
            if (result && result.ok) await primePresentmentPrices(result.products);
            return result;
          } catch (err) {
            if (err.name === 'AbortError') return { aborted: true };
            return { error: true };
          }
        }

        /* ── Render Products ────────────────────────── */

        /**
         * Render the product-card skeletons while results load.
         * @returns {void}
         */
        function showProductSkeletons() {
          productsContainer.replaceChildren();
          for (let i = 0; i < PRODUCT_COUNT; i++) {
            productsContainer.appendChild(skeletonTemplate.content.cloneNode(true));
          }
        }

        /**
         * Show the loading overlay.
         * @returns {void}
         */
        function showLoader() {
          loader.setAttribute('aria-hidden', 'false');
        }

        /**
         * Hide the loading overlay.
         * @returns {void}
         */
        function hideLoader() {
          loader.setAttribute('aria-hidden', 'true');
        }

        /**
         * Render a set of product cards, wiring beacon render/impression/click.
         * @param {Object[]} products
         * @param {boolean} isSearch - true for autocomplete, false for recommend.
         * @param {string|null} [responseId]
         * @param {string|null} [tag]
         * @returns {void}
         */
        function renderProducts(products, isSearch, responseId = null, tag = null) {
          if (activeObserver) {
            activeObserver.disconnect();
            activeObserver = null;
          }
          productsContainer.replaceChildren();
          hideLoader();

          if (!products || products.length === 0) {
            if (isSearch) {
              noResultsEl.setAttribute('aria-hidden', 'false');
            } else {
              productsContainer.appendChild(emptyTemplate.content.cloneNode(true));
            }
            return;
          }

          noResultsEl.setAttribute('aria-hidden', 'true');

          const frag = document.createDocumentFragment();
          products.forEach(function (product, index) {
            const name = product.name || product.title || '';
            const core = (product.mappings && product.mappings.core) || {};
            let priceValue = parseFloat(core.price ?? product.price ?? core.msrp ?? product.msrp ?? 0) || 0;
            let msrpValue = parseFloat(core.msrp ?? product.msrp ?? 0) || 0;
            const imageUrl = product.thumbnailImageUrl || product.imageUrl || product.image || '';
            const handle = product.handle || product.url || '';
            if (!handle) return;
            // Athos indexes base-currency (AUD) feed prices; market shoppers
            // get the exact presentment price primed from the Storefront API.
            const presentment =
              typeof window.AthosAPI.getPresentmentPrice === 'function'
                ? window.AthosAPI.getPresentmentPrice(product.handle || '')
                : null;
            if (presentment) {
              priceValue = presentment.price / 100;
              msrpValue = presentment.compareAt / 100;
            }
            const onSale = msrpValue > 0 && msrpValue > priceValue;
            const promo =
              window.PromoPricing && typeof window.PromoPricing.resolve === 'function'
                ? window.PromoPricing.resolve({
                    priceCents: Math.round(priceValue * 100),
                    tags: product.ss_tags,
                  })
                : null;
            const productUrl = handle.startsWith('/') ? handle : '/products/' + handle;

            const clone = productTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = productUrl;
            a.setAttribute('aria-label', name);

            const imageWrapper = clone.querySelector('.mobile-search-panel__product-image');
            const img = clone.querySelector('img');
            if (imageUrl && img) {
              img.alt = name;
              /* Eagerly load the first few items that will be visible without
                 horizontal scrolling — `loading="lazy"` inside an overflow
                 container can defer them indefinitely on some browsers. */
              if (index < 3) {
                img.setAttribute('loading', 'eager');
                img.setAttribute('fetchpriority', 'high');
              }
              const srcset = buildSrcset(imageUrl, [180, 270, 360, 540]);
              if (srcset) img.setAttribute('srcset', srcset);
              img.src = imageUrl;
            } else if (img) {
              img.remove();
              if (imageWrapper) imageWrapper.removeAttribute('data-skeleton');
            }

            clone.querySelector('.mobile-search-panel__product-title').textContent = name;

            const priceEl = clone.querySelector('.mobile-search-panel__product-price');
            priceEl.replaceChildren();
            if (onSale) {
              const compareSpan = document.createElement('span');
              compareSpan.className = 'mobile-search-panel__price';
              compareSpan.dataset.type = 'sale';
              compareSpan.textContent = window.AthosAPI.formatMoney(msrpValue, MONEY_FORMAT, false);
              const saleSpan = document.createElement('span');
              saleSpan.className = 'mobile-search-panel__price';
              saleSpan.dataset.type = 'discount';
              saleSpan.textContent = window.AthosAPI.formatMoney(priceValue, MONEY_FORMAT, false);
              priceEl.append(compareSpan, saleSpan);
            } else {
              const span = document.createElement('span');
              span.className = 'mobile-search-panel__price';
              span.textContent = window.AthosAPI.formatMoney(priceValue, MONEY_FORMAT, false);
              priceEl.append(span);
            }

            if (promo) {
              const promoText = window.PromoPricing.message(
                window.AthosAPI.formatMoney(promo.priceCents, MONEY_FORMAT, true),
                promo.percent,
              );
              if (promoText) {
                const promoSpan = document.createElement('span');
                promoSpan.className = 'mobile-search-panel__price';
                promoSpan.dataset.type = 'promo';
                promoSpan.textContent = promoText;
                priceEl.append(promoSpan);
              }
            }

            if (responseId && window.AthosBeacon) {
              a.addEventListener('click', function (e) {
                if (!e.target.closest('a[href]')) return;
                if (isSearch) {
                  if (typeof window.AthosBeacon.sendAutocompleteClickthrough === 'function') {
                    window.AthosBeacon.sendAutocompleteClickthrough(responseId, product, false);
                  }
                } else if (tag) {
                  if (typeof window.AthosBeacon.sendRecommendClickthrough === 'function') {
                    window.AthosBeacon.sendRecommendClickthrough(tag, responseId, product);
                  }
                }
              });
            }

            frag.appendChild(a);
          });

          productsContainer.appendChild(frag);

          const cards = Array.from(productsContainer.children);
          if (window.AthosBeacon && responseId) {
            if (!isSearch && tag) {
              window.AthosBeacon.sendRecommendRender(tag, responseId);
            }

            if ('IntersectionObserver' in window) {
              activeObserver = new IntersectionObserver(
                function (entries) {
                  entries.forEach(function (entry) {
                    if (!entry.isIntersecting) return;
                    const index = cards.indexOf(entry.target);
                    if (index === -1) return;
                    activeObserver.unobserve(entry.target);
                    if (products[index]) {
                      if (isSearch) {
                        window.AthosBeacon.sendAutocompleteImpression(responseId, [products[index]], []);
                      } else if (tag) {
                        window.AthosBeacon.sendRecommendImpression(tag, responseId, [products[index]]);
                      }
                    }
                  });
                },
                { threshold: 0.5 }
              );

              cards.forEach(function (card) {
                activeObserver.observe(card);
              });
            } else {
              if (isSearch) {
                window.AthosBeacon.sendAutocompleteImpression(responseId, products, []);
              } else if (tag) {
                window.AthosBeacon.sendRecommendImpression(tag, responseId, products);
              }
            }
          }

          /* Mark already-cached images as loaded so the shimmer fades out
             immediately instead of waiting for a (non-firing) load event. */
          if (window.themeSkeleton && typeof window.themeSkeleton.sweep === 'function') {
            window.themeSkeleton.sweep(productsContainer);
          }

          /* Stagger visibility for animation */
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              const items = productsContainer.querySelectorAll('.mobile-search-panel__product');
              items.forEach(function (item) {
                item.setAttribute('data-visible', 'true');
              });
            });
          });
        }

        /* ── Render Suggestions ─────────────────────── */

        /**
         * Render the autocomplete suggestion terms.
         * @param {string[]} terms
         * @returns {void}
         */
        function renderSuggestions(terms) {
          suggestionsList.replaceChildren();

          if (terms.length === 0) {
            suggestionsCol.setAttribute('aria-hidden', 'true');
            return;
          }

          suggestionsCol.setAttribute('aria-hidden', 'false');
          const frag = document.createDocumentFragment();

          terms.forEach(function (term) {
            const clone = termTemplate.content.cloneNode(true);
            const a = clone.querySelector('a');
            a.href = '/search?q=' + encodeURIComponent(term);
            a.textContent = term;
            a.addEventListener('click', function () {
              window.AthosAPI.saveToHistory(term);
            });
            frag.appendChild(clone);
          });

          suggestionsList.appendChild(frag);
        }

        /* ── Query Change Handler ───────────────────── */

        const handleQueryChange = debounce(async function (query) {
          query = (query || '').trim();

          if (query.length < MIN_QUERY_LENGTH) {
            if (activeAbortController) {
              activeAbortController.abort();
              activeAbortController = null;
            }
            pendingRedirect = null;
            /* Reset to default state */
            suggestionsCol.setAttribute('aria-hidden', 'true');
            suggestionsList.replaceChildren();
            viewAllBtn.setAttribute('aria-hidden', 'true');
            noResultsEl.setAttribute('aria-hidden', 'true');

            if (defaultProducts && defaultProducts.results && defaultProducts.results.length > 0) {
              renderProducts(defaultProducts.results, false, defaultProducts.responseId, defaultProducts.tag);
            } else {
              showProductSkeletons();
            }
            return;
          }

          showLoader();

          const result = await fetchSuggestions(query);
          if (result.aborted) {
            hideLoader();
            return;
          }
          if (result.error) {
            hideLoader();
            noResultsEl.setAttribute('aria-hidden', 'true');
            return;
          }

          renderSuggestions(result.suggestions);
          noResultsEl.setAttribute('aria-hidden', 'true');
          renderProducts(result.products, true, result.responseId);

          const safeRedirect = window.AthosAPI.sanitiseRedirect(result.redirect);
          if (safeRedirect) {
            pendingRedirect = { query: query, redirect: safeRedirect, responseId: result.responseId || '' };
          } else {
            pendingRedirect = null;
          }

          if (result.responseId && window.AthosBeacon) {
            if (typeof window.AthosBeacon.sendAutocompleteRender === 'function') {
              window.AthosBeacon.sendAutocompleteRender(result.responseId);
            }

            if (
              result.banners &&
              result.banners.length > 0 &&
              typeof window.AthosBeacon.sendAutocompleteImpression === 'function'
            ) {
              window.AthosBeacon.sendAutocompleteImpression(result.responseId, [], result.banners);
            }
          }

          if (result.totalResults > 0) {
            totalCountEl.textContent = result.totalResults;
            viewAllBtn.href = '/search?q=' + encodeURIComponent(query);
            viewAllBtn.setAttribute('aria-hidden', 'false');
          } else {
            viewAllBtn.setAttribute('aria-hidden', 'true');
          }
        }, 300);

        /* ── Render Message ─────────────────────────── */

        /**
         * Render a single centred message inside the products container.
         * @param {string} msg
         * @returns {void}
         */
        function renderMessage(msg) {
          productsContainer.replaceChildren();
          const p = document.createElement('p');
          p.textContent = msg;
          p.className = 'mobile-search-panel__empty';
          productsContainer.appendChild(p);
        }

        /* ── Open / Close hooks (driven by the surface manager) ── */

        /**
         * Heavy open work — renders recent, trending, default products and
         * (if prefilled) the query results. Called by DrawerDialog after the
         * dialog has opened (or after the replace() View Transition snapshot).
         * @param {Object} [detail={}] - { prefillQuery?: string }
         * @returns {Promise<void>}
         */
        host.onOpen = async function (detail = {}) {
          const prefillQuery = (detail && detail.prefillQuery) || '';

          if (searchInput) searchInput.setAttribute('aria-expanded', 'true');

          if (prefillQuery && searchInput) {
            searchInput.value = prefillQuery;
            clearBtn.toggleAttribute('data-visible', prefillQuery.length > 0);
          }

          /* Focus input after frame so transition can begin */
          requestAnimationFrame(function () {
            if (searchInput) searchInput.focus();
          });

          if (!window.AthosAPI) {
            renderMessage(host.dataset.textSearchError);
            suggestionsCol.setAttribute('aria-hidden', 'true');
            viewAllBtn.setAttribute('aria-hidden', 'true');
            noResultsEl.setAttribute('aria-hidden', 'true');
            hideLoader();
            return;
          }

          /* Render recent searches */
          renderRecent();

          /* Reset suggestions column */
          suggestionsCol.setAttribute('aria-hidden', 'true');
          viewAllBtn.setAttribute('aria-hidden', 'true');
          noResultsEl.setAttribute('aria-hidden', 'true');

          /* Show product skeletons */
          showProductSkeletons();

          try {
            /* Fetch trending (top searches) */
            const terms = await fetchTrending();
            renderTrending(terms);
          } catch (err) {
            renderTrending([]);
          }

          /* If there's already a query, run it */
          if (searchInput && searchInput.value.trim().length >= MIN_QUERY_LENGTH) {
            handleQueryChange(searchInput.value.trim());
          } else {
            /* Load default recommended products */
            try {
              if (!defaultProducts) {
                defaultProducts = await fetchDefaultProducts();
              }
            } catch (err) {
              defaultProducts = null;
            }

            // Re-check input value after awaits
            if (searchInput && searchInput.value.trim().length >= MIN_QUERY_LENGTH) {
              return;
            }

            if (defaultProducts && defaultProducts.results && defaultProducts.results.length > 0) {
              renderProducts(defaultProducts.results, false, defaultProducts.responseId, defaultProducts.tag);
            } else {
              renderMessage(host.dataset.textSearchError);
            }
          }
        };

        /**
         * Teardown when the drawer closes. Scroll-lock, focus restore and the
         * search:close event are handled by the base/manager.
         * @returns {void}
         */
        host.onClose = function () {
          if (searchInput) searchInput.setAttribute('aria-expanded', 'false');
          pendingRedirect = null;

          /* Clear input */
          if (searchInput) {
            searchInput.value = '';
            searchInput.blur();
          }
          if (clearBtn) clearBtn.removeAttribute('data-visible');

          /* Cancel a debounced query render still in flight so it cannot fire
             impression/render beacons after the drawer has torn down. */
          if (handleQueryChange && typeof handleQueryChange.cancel === 'function') {
            handleQueryChange.cancel();
          }

          /* Cancel in-flight requests */
          if (activeAbortController) {
            activeAbortController.abort();
            activeAbortController = null;
          }

          /* Disconnect the impression observer so no product-impression
             beacons fire against a torn-down responseId (issue 2 — beacon 400
             burst). A fresh render re-creates it. */
          if (activeObserver) {
            activeObserver.disconnect();
            activeObserver = null;
          }
        };

        /* ── Input Events ───────────────────────────── */

        if (searchInput) {
          searchInput.addEventListener('input', function () {
            const val = searchInput.value.trim();
            clearBtn.toggleAttribute('data-visible', searchInput.value.length > 0);
            handleQueryChange(val);
          });

          searchInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') {
              e.preventDefault();
              const q = searchInput.value.trim();
              if (pendingRedirect && pendingRedirect.query === q) {
                if (window.AthosBeacon && typeof window.AthosBeacon.sendAutocompleteRedirect === 'function') {
                  window.AthosBeacon.sendAutocompleteRedirect(pendingRedirect.responseId, pendingRedirect.redirect);
                }
                window.location.href = pendingRedirect.redirect;
                return;
              }
              if (q.length > 0) {
                window.AthosAPI.saveToHistory(q);
                window.location.href = '/search?q=' + encodeURIComponent(q);
              }
            }
          });
        }

        /* ── Clear Button ───────────────────────────── */

        if (clearBtn) {
          clearBtn.addEventListener('click', function () {
            searchInput.value = '';
            clearBtn.removeAttribute('data-visible');
            handleQueryChange('');
            searchInput.focus();
          });
        }

        /* ── Close Button ───────────────────────────── */

        const closeBtn = host.querySelector('[data-msearch-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            utils.surfaces.close('mobile-search');
          });
        }

        /* ── View All Click ─────────────────────────── */

        if (viewAllBtn) {
          viewAllBtn.addEventListener('click', function () {
            const q = searchInput ? searchInput.value.trim() : '';
            if (q.length > 0) {
              window.AthosAPI.saveToHistory(q);
            }
          });
        }

        /* ── Responsive: close if resized past mobile breakpoint ── */

        const mobileQuery = window.matchMedia('(max-width: 767px)');
        mobileQuery.addEventListener('change', function (e) {
          if (!e.matches && utils.surfaces.current() === 'mobile-search') {
            utils.surfaces.close('mobile-search');
          }
        });
      }
    }

    if (!window.customElements.get('mobile-search-drawer')) {
      window.customElements.define('mobile-search-drawer', MobileSearchDrawer);
    }
  })();
